import { getStoreManager } from '@affine/core/blocksuite/manager/store';
import {
  AwarenessStore,
  type Doc,
  type ExtensionType,
  type GetStoreOptions,
  isStoreExtensionConstructor,
  StoreContainer,
  type YBlock,
} from '@blocksuite/affine/store';
import { Awareness } from 'y-protocols/awareness.js';
import * as Y from 'yjs';

import type { WorkspaceImpl } from './workspace';

type DocOptions = {
  id: string;
  collection: WorkspaceImpl;
  doc: Y.Doc;
};

type DisposeOptions = {
  clearBlocks?: boolean;
};

function normalizeStoreExtensions(
  extensions: ExtensionType[]
): ExtensionType[] {
  const seenExtensions = new Set<ExtensionType>();
  const seenStoreExtensionKeys = new Set<string>();
  const normalized: ExtensionType[] = [];

  for (const extension of extensions) {
    if (seenExtensions.has(extension)) {
      continue;
    }

    if (isStoreExtensionConstructor(extension)) {
      if (seenStoreExtensionKeys.has(extension.key)) {
        continue;
      }
      seenStoreExtensionKeys.add(extension.key);
    }

    seenExtensions.add(extension);
    normalized.push(extension);
  }

  return normalized;
}

export class DocImpl implements Doc {
  private readonly _collection: WorkspaceImpl;

  private readonly _storeContainer: StoreContainer;

  private _loaded = false;

  /** Indicate whether the block tree is ready */
  private _ready = false;

  private _disconnectAwareness: (() => void) | null = null;

  private _awarenessConnected = false;

  protected readonly _yBlocks: Y.Map<YBlock>;

  /**
   * @internal Used for convenient access to the underlying Yjs map,
   * can be used interchangeably with ySpace
   */
  protected readonly _ySpaceDoc: Y.Doc;

  readonly storeExtensions: ExtensionType[] = [];

  readonly awarenessStore: AwarenessStore;

  readonly id: string;

  readonly rootDoc: Y.Doc;

  get blobSync() {
    return this.workspace.blobSync;
  }

  get workspace() {
    return this._collection;
  }

  get isEmpty() {
    return this._yBlocks.size === 0;
  }

  get loaded() {
    return this._loaded;
  }

  get meta() {
    return this.workspace.getDocMeta(this.id);
  }

  get ready() {
    return this._ready;
  }

  get spaceDoc() {
    return this._ySpaceDoc;
  }

  get yBlocks() {
    return this._yBlocks;
  }

  constructor({ id, collection, doc }: DocOptions) {
    this.id = id;
    this.rootDoc = doc;

    const spaceDoc = new Y.Doc({ guid: this.id });
    spaceDoc.clientID = this.rootDoc.clientID;
    this._ySpaceDoc = spaceDoc;

    this.awarenessStore = new AwarenessStore(new Awareness(this._ySpaceDoc));

    this._yBlocks = this._ySpaceDoc.getMap('blocks');
    this._collection = collection;
    this._storeContainer = new StoreContainer(this);
  }

  clear() {
    this._yBlocks.clear();
  }

  get removeStore() {
    return this._storeContainer.removeStore;
  }

  private _destroy() {
    this.awarenessStore.destroy();
    this._ySpaceDoc.destroy();
    this._loaded = false;
  }

  dispose({ clearBlocks = true }: DisposeOptions = {}) {
    this._disconnectAwareness?.();
    this._disconnectAwareness = null;
    this._awarenessConnected = false;
    this._destroy();

    if (clearBlocks && this.ready) {
      this._yBlocks.clear();
    }
  }

  getStore({
    readonly,
    query,
    provider,
    extensions,
    id,
  }: GetStoreOptions = {}) {
    const storeExtensions = getStoreManager()
      .config.init()
      .featureFlag(this.workspace.featureFlagService)
      .value.get('store');
    const exts = storeExtensions
      .concat(extensions ?? [])
      .concat(this.storeExtensions);

    return this._storeContainer.getStore({
      id,
      readonly,
      query,
      provider,
      extensions: normalizeStoreExtensions(exts),
    });
  }

  load(initFn?: () => void): this {
    if (this.ready) {
      return this;
    }

    initFn?.();
    this.spaceDoc.load();

    this.workspace.onLoadDoc?.(this.spaceDoc);
    this._loaded = true;
    this._ready = true;

    return this;
  }

  connectAwareness(): void {
    if (this._awarenessConnected) {
      return;
    }
    if (!(this.workspace.shouldLoadAwareness?.(this.id) ?? true)) {
      return;
    }
    this._awarenessConnected = true;
    this._disconnectAwareness =
      this.workspace.onLoadAwareness?.(this.awarenessStore.awareness) ?? null;
  }

  remove() {
    this._destroy();
  }
}
